import * as Y from 'yjs'

/**
 * The browser half of the grid part kind — the mirror of backend/domain/grid.ts (the rules) and
 * backend/adapters/room/grid-crdt.ts (the Yjs layout). The web app cannot import backend source,
 * so this is a hand-maintained parity file, exactly like lib/diff.ts and lib/sections.ts.
 * KEEP THE TWO IN SYNC.
 *
 *   Y.Map('meta')          { kind: 'grid', v: 1 }
 *   Y.Map('columns')       colId -> Y.Map { label, type, width?, numFmt? }
 *   Y.Array('columnOrder') colId[]
 *   Y.Map('rows')          rowId -> Y.Map { <colId>: string | number }
 *   Y.Array('rowOrder')    rowId[]
 *   Y.Text('content')      EMPTY in a grid room
 *
 * Rows are an id-keyed map plus an order array rather than a `Y.Array` of `Y.Map`, because
 * `Y.Array` has no move op: a reorder there is delete+insert, which destroys the moved map's
 * subtree and with it any concurrent cell edit. Sorting a pricing grid would silently drop a
 * teammate's typing.
 */

/** Parity: backend/domain/grid.ts#PartKind. 'slides' = a slide deck (its editor is not built yet). */
export type PartKind = 'markdown' | 'grid' | 'slides'

export type GridCellType = 'text' | 'number' | 'money' | 'percent' | 'date'

export const GRID_CELL_TYPES: GridCellType[] = ['text', 'number', 'money', 'percent', 'date']

export type GridCellValue = string | number | null

export interface GridColumn {
  id: string
  label: string
  type: GridCellType
  width?: number
  numFmt?: string
}

export interface GridRow {
  id: string
  cells: Record<string, GridCellValue>
}

export interface GridSnapshot {
  columns: GridColumn[]
  rows: GridRow[]
}

/** One cell's before/after, with the labels frozen so the history log reads without ids.
 *  Parity: backend/domain/grid.ts#CellChange. */
export interface CellChange {
  rowId: string
  colId: string
  colLabel?: string
  rowLabel?: string
  before: string
  after: string
}

/** A structural change, grouped by kind. Parity: backend/domain/grid.ts#GridStructureChange. */
export interface GridStructureChange {
  op: string
  count: number
  label?: string
}

/** The cell range a revision claims — stable ids, never an A1 rectangle (rows move).
 *  Parity: backend/domain/revisions.ts#CellScope. */
export interface CellScope {
  rowIds: string[]
  colIds: string[]
}

// ─── Rules (parity with backend/domain/grid.ts) ───────────────────────────────

/**
 * Reconcile an id-keyed collection with its order array: listed-and-present ids first (once),
 * then anything present but unlisted, then nothing else. A merge routinely leaves the two
 * disagreeing — a peer inserting while another reorders — and neither may lose data.
 */
export function orderedIds(known: Iterable<string>, order: readonly string[] = []): string[] {
  const present = new Set(known)
  const out: string[] = []
  const seen = new Set<string>()
  for (const id of order) {
    if (!present.has(id) || seen.has(id)) continue
    seen.add(id)
    out.push(id)
  }
  for (const id of present) if (!seen.has(id)) out.push(id)
  return out
}

export function formatCell(value: GridCellValue): string {
  if (value === null || value === undefined) return ''
  return typeof value === 'number' ? String(value) : value
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ][\d:.]+(?:Z|[+-]\d{2}:?\d{2})?)?$/
/** Spaces (`\s` covers U+00A0/U+202F, the French thousands separator), currency marks, percent. */
const NUMERIC_NOISE = /[\s\u00A0\u202F€$£%]/g

/** Parse a number the way a human writes one, or null — the LAST of `.`/`,` is the decimal mark. */
export function parseNumber(text: string): number | null {
  const cleaned = text.replace(NUMERIC_NOISE, '')
  if (cleaned === '' || !/\d/.test(cleaned)) return null
  const lastDot = cleaned.lastIndexOf('.')
  const lastComma = cleaned.lastIndexOf(',')
  let normalised: string
  if (lastDot >= 0 && lastComma >= 0) {
    normalised = lastComma > lastDot ? cleaned.replace(/\./g, '').replace(',', '.') : cleaned.replace(/,/g, '')
  } else if (lastComma >= 0) {
    normalised = /^-?\d{1,3}(?:,\d{3})+$/.test(cleaned) ? cleaned.replace(/,/g, '') : cleaned.replace(',', '.')
  } else {
    normalised = cleaned
  }
  const n = Number(normalised)
  return Number.isFinite(n) ? n : null
}

/**
 * Coerce raw input into the stored form for `type`. FORGIVING and never lossy: input that does not
 * parse stays a string rather than becoming null — a pricing cell reading "on request" must
 * survive, and the export then writes that one as text and the rest of the column as numbers.
 */
export function coerceCell(raw: unknown, type: GridCellType): GridCellValue {
  if (raw === null || raw === undefined) return null
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null
  const text = String(raw).trim()
  if (text === '') return null
  if (type === 'text') return text
  if (type === 'date') {
    const m = ISO_DATE.exec(text)
    return m ? `${m[1]}-${m[2]}-${m[3]}` : text
  }
  const n = parseNumber(text)
  return n === null ? text : n
}

export function columnById(grid: GridSnapshot, colId: string): GridColumn | undefined {
  return grid.columns.find((c) => c.id === colId)
}

export function cellAt(grid: GridSnapshot, rowId: string, colId: string): GridCellValue {
  return grid.rows.find((r) => r.id === rowId)?.cells[colId] ?? null
}

/** The column a row is NAMED by in a log line — the first text column, else the first. */
export function labelColumnId(grid: GridSnapshot): string | undefined {
  return (grid.columns.find((c) => c.type === 'text') ?? grid.columns[0])?.id
}

export function rowLabel(grid: GridSnapshot, rowId: string): string {
  const colId = labelColumnId(grid)
  return colId ? formatCell(cellAt(grid, rowId, colId)) : ''
}

// ─── The Yjs layer (parity with backend/adapters/room/grid-crdt.ts) ───────────

/** Is this room a grid? Reading `meta` on a prose doc materialises the type but writes nothing. */
export function isGridDoc(ydoc: Y.Doc): boolean {
  return ydoc.getMap('meta').get('kind') === 'grid'
}

function field(raw: unknown, key: string): unknown {
  if (raw instanceof Y.Map) return raw.get(key)
  if (raw && typeof raw === 'object') return (raw as Record<string, unknown>)[key]
  return undefined
}

function readColumn(id: string, raw: unknown): GridColumn {
  const label = field(raw, 'label')
  const type = field(raw, 'type')
  const width = field(raw, 'width')
  const numFmt = field(raw, 'numFmt')
  return {
    id,
    label: typeof label === 'string' ? label : id,
    type: GRID_CELL_TYPES.includes(type as GridCellType) ? (type as GridCellType) : 'text',
    ...(typeof width === 'number' && Number.isFinite(width) ? { width } : {}),
    ...(typeof numFmt === 'string' && numFmt !== '' ? { numFmt } : {}),
  }
}

function readCellValue(raw: unknown): GridCellValue {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null
  if (typeof raw === 'string') return raw
  if (typeof raw === 'boolean') return raw ? 'true' : 'false'
  return null
}

/** The flattened snapshot of a grid room. Answers an empty grid on a prose room. */
export function readGrid(ydoc: Y.Doc): GridSnapshot {
  const cols = ydoc.getMap('columns')
  const colIds = orderedIds(cols.keys(), ydoc.getArray<string>('columnOrder').toArray())
  const columns = colIds.map((id) => readColumn(id, cols.get(id)))
  const rows = ydoc.getMap('rows')
  const rowIds = orderedIds(rows.keys(), ydoc.getArray<string>('rowOrder').toArray())
  return {
    columns,
    rows: rowIds.map((id) => {
      const record = rows.get(id)
      const cells: Record<string, GridCellValue> = {}
      for (const col of columns) {
        const value = readCellValue(field(record, col.id))
        if (value !== null) cells[col.id] = value
      }
      return { id, cells }
    }),
  }
}

/** A collision-free id for a new row/column. Uuid-shaped, so it can never meet a `c1`/`r1` seed. */
export function newId(prefix: 'r' | 'c'): string {
  const rand = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`
  return `${prefix}-${rand}`
}

/** Write one cell. `null` DELETES the key: absent is the canonical "empty cell". */
export function writeCell(ydoc: Y.Doc, rowId: string, colId: string, value: GridCellValue): void {
  const record = ydoc.getMap('rows').get(rowId)
  if (!(record instanceof Y.Map)) return
  ydoc.transact(() => {
    if (value === null) {
      if (record.has(colId)) record.delete(colId)
    } else if (record.get(colId) !== value) {
      record.set(colId, value)
    }
  })
}

export function insertRow(ydoc: Y.Doc, at?: number): string {
  const id = newId('r')
  const order = ydoc.getArray<string>('rowOrder')
  ydoc.transact(() => {
    ydoc.getMap('rows').set(id, new Y.Map<unknown>())
    order.insert(at === undefined ? order.length : Math.max(0, Math.min(at, order.length)), [id])
  })
  return id
}

export function deleteRow(ydoc: Y.Doc, rowId: string): void {
  const order = ydoc.getArray<string>('rowOrder')
  ydoc.transact(() => {
    ydoc.getMap('rows').delete(rowId)
    const at = order.toArray().indexOf(rowId)
    if (at !== -1) order.delete(at, 1)
  })
}

/**
 * Move a row. Only ever splices a STRING in the order array — the cell data is untouched, which is
 * why a concurrent edit to the moved row survives (see the header).
 */
export function moveRow(ydoc: Y.Doc, rowId: string, to: number): void {
  const order = ydoc.getArray<string>('rowOrder')
  const list = order.toArray()
  const from = list.indexOf(rowId)
  const target = Math.max(0, Math.min(to, list.length - 1))
  if (from === -1 || from === target) return
  ydoc.transact(() => {
    order.delete(from, 1)
    order.insert(target, [rowId])
  })
}

export function insertColumn(ydoc: Y.Doc, label: string, type: GridCellType = 'text', at?: number): string {
  const id = newId('c')
  const order = ydoc.getArray<string>('columnOrder')
  ydoc.transact(() => {
    const record = new Y.Map<unknown>()
    record.set('label', label)
    record.set('type', type)
    ydoc.getMap('columns').set(id, record)
    order.insert(at === undefined ? order.length : Math.max(0, Math.min(at, order.length)), [id])
  })
  return id
}

export function deleteColumn(ydoc: Y.Doc, colId: string): void {
  const order = ydoc.getArray<string>('columnOrder')
  const rows = ydoc.getMap('rows')
  ydoc.transact(() => {
    ydoc.getMap('columns').delete(colId)
    const at = order.toArray().indexOf(colId)
    if (at !== -1) order.delete(at, 1)
    // The address is gone, so the data is too.
    for (const rowId of rows.keys()) {
      const record = rows.get(rowId)
      if (record instanceof Y.Map && record.has(colId)) record.delete(colId)
    }
  })
}

export function updateColumn(
  ydoc: Y.Doc,
  colId: string,
  patch: { label?: string; type?: GridCellType; width?: number },
): void {
  const record = ydoc.getMap('columns').get(colId)
  if (!(record instanceof Y.Map)) return
  ydoc.transact(() => {
    for (const [key, value] of Object.entries(patch)) {
      if (value !== undefined && record.get(key) !== value) record.set(key, value)
    }
  })
}

// ─── The one-way escape hatch: a prose pipe-table becomes a grid part ─────────

/** A GFM pipe-table row, with `\|` un-escaped back to a literal bar. */
function splitPipeRow(line: string): string[] {
  const cells: string[] = []
  let current = ''
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i]
    if (ch === '\\' && line[i + 1] === '|') {
      current += '|'
      i += 1
    } else if (ch === '|') {
      cells.push(current)
      current = ''
    } else {
      current += ch
    }
  }
  cells.push(current)
  // A GFM row is bounded by pipes, so the split yields an empty cell at each end.
  if (cells[0]?.trim() === '') cells.shift()
  if (cells[cells.length - 1]?.trim() === '') cells.pop()
  return cells.map((c) => c.trim())
}

const SEPARATOR_ROW = /^[\s|:-]+$/

/**
 * Parse a GFM pipe-table into a grid — the "convert this table to a grid part" action.
 *
 * ONE-WAY, deliberately. The reverse (a grid rendered as a pipe-table) already exists as the
 * backend's lossy `gridToMarkdown` projection; going back the other way would mean deciding what
 * to do with a multi-line cell, which is the very thing a pipe-table cannot express.
 *
 * Column types are INFERRED, once, at conversion: a column whose non-empty cells all parse as
 * numbers becomes `number`. Deliberately conservative — it is a starting point the member retypes
 * in one click, not a guess to build on. Returns null when the text is not a table.
 */
export function parsePipeTable(markdown: string): GridSnapshot | null {
  const lines = markdown.split(/\r?\n/).filter((l) => l.trim() !== '')
  if (lines.length < 2) return null
  const header = splitPipeRow(lines[0]!)
  if (header.length === 0 || !SEPARATOR_ROW.test(lines[1]!)) return null

  const bodies = lines.slice(2).map(splitPipeRow)
  const columns: GridColumn[] = header.map((label, i) => {
    const values = bodies.map((r) => r[i] ?? '').filter((v) => v !== '')
    const numeric = values.length > 0 && values.every((v) => parseNumber(v) !== null)
    return { id: `c${i + 1}`, label: label || `Column ${i + 1}`, type: numeric ? 'number' : 'text' }
  })
  const rows: GridRow[] = bodies.map((body, r) => {
    const cells: Record<string, GridCellValue> = {}
    columns.forEach((col, i) => {
      const value = coerceCell(body[i] ?? '', col.type)
      if (value !== null) cells[col.id] = value
    })
    return { id: `r${r + 1}`, cells }
  })
  return { columns, rows }
}
